import { useState, useEffect, useRef } from "react";

interface GeoPosition {
  lat: number;
  lng: number;
}

interface UseGeolocationReturn {
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  error: string | null;
}

export interface GpsDynamicVariables
  extends Record<string, string | number | boolean> {
  user_lat: number;
  user_lng: number;
}

const MIN_UPDATE_DISTANCE_M = 50;

export function buildGpsDynamicVariables(
  enabled: boolean,
  latitude: number | null,
  longitude: number | null
): GpsDynamicVariables | undefined {
  if (!enabled || latitude === null || longitude === null) return undefined;
  return { user_lat: latitude, user_lng: longitude };
}

export function shouldUpdatePosition(
  prev: GeoPosition | null,
  next: GeoPosition,
  minDistanceM: number
): boolean {
  if (!prev) return true;
  const dLat = (next.lat - prev.lat) * 111320;
  const dLng =
    (next.lng - prev.lng) * 111320 * Math.cos((prev.lat * Math.PI) / 180);
  const dist = Math.sqrt(dLat * dLat + dLng * dLng);
  return dist >= minDistanceM;
}

export function useGeolocation(enabled?: boolean): UseGeolocationReturn {
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [accuracy, setAccuracy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const lastPositionRef = useRef<GeoPosition | null>(null);

  useEffect(() => {
    if (!enabled) {
      lastPositionRef.current = null;
      setLatitude(null);
      setLongitude(null);
      setAccuracy(null);
      setError(null);
      return;
    }

    if (!navigator.geolocation) {
      setError("Geolocation is not supported");
      return;
    }

    let active = true;
    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        if (!active) return;
        const next: GeoPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        if (
          shouldUpdatePosition(
            lastPositionRef.current,
            next,
            MIN_UPDATE_DISTANCE_M
          )
        ) {
          lastPositionRef.current = next;
          setLatitude(next.lat);
          setLongitude(next.lng);
          setAccuracy(position.coords.accuracy);
          setError(null);
        }
      },
      (err) => {
        if (!active) return;
        setError(err.message);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 30000,
      }
    );

    return () => {
      active = false;
      navigator.geolocation.clearWatch(watchId);
    };
  }, [enabled]);

  return { latitude, longitude, accuracy, error };
}
